<template>
  <div class="py-2 px-2 container-fluid modern-scrollbar" v-if="!profile || inProgress">
    <div class="profile-grid">
      <!-- Main skeleton -->
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between mb-4">
            <NSkeleton text style="width: 40%; height: 1.75rem;" />
            <NSkeleton text style="width: 6rem; height: 2rem;" />
          </div>
          <div class="meta-row mb-3">
            <NSkeleton text style="width: 8rem;" />
            <NSkeleton text style="width: 10rem;" />
            <NSkeleton text style="width: 7rem;" />
          </div>
          <NSkeleton text :repeat="4" />
          <NSkeleton text style="width: 75%" />
          <div class="mt-3">
            <NSkeleton text :repeat="3" />
            <NSkeleton text style="width: 55%" />
          </div>
        </div>
      </div>
      <!-- Sidebar skeleton -->
      <div class="card area-sidebar">
        <div class="card-body">
          <!-- Ownership block -->
          <div class="mb-4">
            <NSkeleton text style="width: 50%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 80%" />
            <NSkeleton text style="width: 65%" />
          </div>
          <!-- Markets block -->
          <div class="mb-4">
            <NSkeleton text style="width: 45%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 90%" />
            <NSkeleton text style="width: 70%" />
            <NSkeleton text style="width: 55%" />
          </div>
          <!-- Products block -->
          <div class="mb-4">
            <NSkeleton text style="width: 40%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 85%" />
            <NSkeleton text style="width: 75%" />
          </div>
        </div>
      </div>
      <!-- Map skeleton -->
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <NSkeleton style="width: 100%; height: 400px;" />
          <div class="m-2 mx-3">
            <NSkeleton text style="width: 50%" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="py-2 px-2 container-fluid modern-scrollbar" v-else>
    <div class="profile-grid">

      <!-- Main Info Card -->
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <h3 class="mb-4 proxima-blue-text">
              {{ profile.short_Name }}
            </h3>
            <div>
              <NButton type="info" @click="addNote" color="#213ce5"
                       style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; border-radius: 3px !important;"
                       @mouseenter="e => e.currentTarget.style.border = '1px solid #00aeef'"
                       @mouseleave="e => e.currentTarget.style.border = '1px solid transparent'">
                {{
                  has_notes("ASSET", route)
                      ? $t("AssetProfile.viewNotesButton")
                      : $t("AssetProfile.addNoteButton")
                }}
              </NButton>
            </div>
          </div>
          <div class="meta-row mb-3">
            <div class="text-sm">
              <span class="text-muted pe-1">{{ $t("AssetProfile.typeHeader") }}:</span>
              <span class="proxima-blue-text font-weight-bold">{{ profile.type }}</span>
            </div>
            <div class="text-sm">
              <span class="text-muted pe-1">{{ $t("AssetProfile.operatorHeader") }}:</span>
              <span class="proxima-blue-text font-weight-bold">{{ profile.operatorName }}</span>
            </div>
            <div class="text-sm">
              <span class="text-muted pe-1">{{ $t("AssetProfile.statusHeader") }}:</span>
              <span class="proxima-blue-text font-weight-bold">{{ profile.stage }}</span>
            </div>
          </div>
          <InfoText
              class="mb-3"
              v-if="profile.profile"
              :title="$t('AssetProfile.profileHeader')"
              :contents="profile.profile"
          />
          <InfoText
              class="mb-3"
              v-if="profile.projects"
              :title="$t('AssetProfile.projectsHeader')"
              :contents="profile.projects"
          />
        </div>
      </div>

      <!-- Sidebar Card -->
      <div class="card area-sidebar">
        <div class="card-body">
          <div class="mb-4" v-if="owners.length">
            <h5 class="proxima-blue-text">{{ $t("AssetProfile.ownershipHeader") }}</h5>
            <div
                class="text-sm"
                v-for="nth of owners || []"
                :key="nth.name"
                @click="goToCompanyProfile(nth.id)"
                style="cursor: pointer"
            >
              {{ nth.name }}{{ nth.percentage ? ` (${nth.percentage}%)` : "" }}
            </div>
          </div>
          <div
              class="mb-4"
              v-if="profile?.primaryCommodities || profile?.byProductCommodities || profile?.nonCommercialCommodities"
          >
            <h5 class="proxima-blue-text">{{ $t("AssetProfile.marketsHeader") }}</h5>
            <div class="text-sm" v-if="profile?.primaryCommodities?.length">
              {{ $t("AssetProfile.primaryHeader") }}: {{ formatCommodities(profile?.primaryCommodities) }}
            </div>
            <div class="text-sm" v-if="profile?.byProductCommodities?.length">
              {{ $t("AssetProfile.byProductHeader") }}: {{ formatCommodities(profile?.byProductCommodities) }}
            </div>
            <div class="text-sm" v-if="profile?.nonCommercialCommodities?.length">
              {{ $t("AssetProfile.nonCommercialHeader") }}: {{ formatCommodities(profile?.nonCommercialCommodities) }}
            </div>
          </div>
          <div class="mb-4" v-if="products.length">
            <h5 class="proxima-blue-text">{{ $t("AssetProfile.productsHeader") }}</h5>
            <div
                class="text-sm"
                v-for="nth of products || []"
                :key="nth.name"
            >
              {{ nth.productName }}
              {{ nth.capacity ? `- ${nth.capacity} ${nth.unit != null ? nth.unit : ""}` : "" }}
              {{ nth.grossContained === "Gross Weight" ? "" : nth.grossContained }}
              <span class="text-capi">{{ nth.status ? `(${nth.status})` : "" }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Card -->
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <iframe
              id="gmap_canvas"
              width="100%"
              height="400"
              :src="`https://maps.google.com/maps?q=${
              profile.coordinates?.replace(' ', '') || profile.countryName
            }&t=k&z=${profile.coordinates ? 15 : 4}&ie=UTF8&iwloc=&output=embed`"
              frameborder="0"
              scrolling="no"
              marginheight="0"
              marginwidth="0"
          ></iframe>
          <div class="m-2 mx-3">
            {{
              $t("AssetProfile.locationFooter", {
                country: profile.countryName,
                pinAccuracy: formatPinAccuracy(profile),
              })
            }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, computed, nextTick, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ROUTES } from "../../routes/names";
import { NSkeleton, NButton } from "naive-ui";
import {
  MOUNTED_ASSET_PROFILE as profile,
  getAssetProfile,
  getAssetProfileDetails,
  inProgress,
  MOUNTED_ASSET_PROFILE_DETAILS 
} from "../../composables/profiles/asset";
import InfoText from "../../components/dumb/ProfileInfoText";
import {
  NOTES_WINDOW,
  SHOW_ALL_NOTES,
  has_notes,
} from "../../composables/notes/list";
import ProfilesMenu from "../../components/ProfilesMenu.vue";
import {
  getCompanyProfile,
  MOUNTED_COMPANY_PROFILE,
} from "../../composables/profiles/company";
import { logUserAction, USER_ACTIONS } from "@/composables/userLogs";

const route = useRoute();
const router = useRouter();

const loadAssetProfile = async () => {
  const uuid = route.params?.id;
  const element = route.query?.element;
  // if (!uuid || !element) router.push({ name: ROUTES.PROXIMA_PROFILES.name });
  if (uuid && element) {
    await getAssetProfile({ element, uuid });

    // Track asset profile access
    const assetName = profile.value?.short_Name || profile.value?.name || uuid;
    const operator = profile.value?.operatorName || 'Unknown Operator';
    logUserAction(
      USER_ACTIONS.VIEW_ASSET_PROFILE,
      `Viewed Asset Profile: "${assetName}" (Operator: "${operator}", Commodity: "${element}")`,
      { endpoint: `/asset/${uuid}` }
    );
  }
};

onMounted(loadAssetProfile);

// Watch for route parameter changes to reload asset data when navigating to different assets
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await loadAssetProfile();
    }
  }
);

// Also watch for element query parameter changes
watch(
  () => route.query.element,
  async (newElement, oldElement) => {
    if (newElement && newElement !== oldElement) {
      await loadAssetProfile();
    }
  }
);

onBeforeUnmount(() => {
  profile.value = null;
});

const addNote = () => {
  NOTES_WINDOW.value = true;
  SHOW_ALL_NOTES.value = false;
};

const owners = computed(() => {
  return (profile.value?.owners || []).filter(
    (nth) => nth.companyName !== null
  );
});

const formatCommodities = (elements) => {
  return (elements || []).join(", ");
};

const products = computed(() => {
  return (profile.value?.products || []).filter((nth) => nth.name !== null);
});
// const goToCompanyProfile = async (id) => {
//   const element = route.query.element;
//   await getCompanyProfile({ element, uuid: null, id });
//   nextTick(() => {
//     if (MOUNTED_COMPANY_PROFILE.value?.uuid) {
//       router.push({
//         name: ROUTES.COMPANY_PROFILE.name,
//         params: { id: MOUNTED_COMPANY_PROFILE.value?.uuid },
//         query: { element },
//       });
//     } else window.$message.info("No profile found");
//   });
// };

const goToCompanyProfile = async (id) => {
  const element = route.query.element;
  const profileType = 'COMPANY'
  await getCompanyProfile({ element, uuid: null, id });
  nextTick(() => {
    if (MOUNTED_COMPANY_PROFILE.value?.uuid) {
      const uuid = MOUNTED_COMPANY_PROFILE.value?.uuid
      router.push({
        name: ROUTES.PROXIMA_PROFILES.name,
        // params: { id: MOUNTED_COMPANY_PROFILE.value?.uuid },
        query: {  _: Math.random(), profileType, uuid, element },
      });
    } else window.$message.info("No profile found");
  });
};

const formatPinAccuracy = (profile) => {
  return `${profile.type} / ${profile.nearestTown || "Nearest town"} / ${
    profile.province || "Province"
  }`;
};
</script>

<style lang="scss" scoped>
* {
  border-radius: 0 !important;
}

.profile-grid {
  display: grid;
  gap: 0.5rem;

  // Mobile: single column
  grid-template-columns: 1fr;
  grid-template-areas:
    "main"
    "sidebar"
    "map";

  // Tablet: two columns, sidebar narrower
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }

  // Desktop: main takes 2/3, sidebar takes 1/3
  @media (min-width: 992px) {
    grid-template-columns: 2fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }
}

.area-main    { grid-area: main; }
.area-sidebar { grid-area: sidebar; }
.area-map     { grid-area: map; }

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}
</style>
